import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  AgentDefinition,
  AgentScript,
  AgentVersion,
  AgentTestRequest,
  AgentTestResult,
  CreateAgentRequest,
  UpdateAgentRequest,
  CreateScriptRequest,
  UpdateScriptRequest,
} from '@/types/agent';
import { getAccessToken } from '@/utils/jwt';

export const studioApi = createApi({
  reducerPath: 'studioApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Agent', 'Script', 'Version', 'Skill'],
  endpoints: (builder) => ({
    listAgents: builder.query<AgentDefinition[], { status?: string }>({
      query: (params) => ({ url: '/studio/agents', params }),
      transformResponse: (response: AgentDefinition[] | { success: boolean; error?: string }) => {
        // Handle both array response and object response
        if (Array.isArray(response)) {
          return response;
        }
        if ('error' in response) {
          throw new Error(response.error ?? 'Failed to fetch agents');
        }
        return [];
      },
      providesTags: ['Agent'],
    }),
    getAgent: builder.query<AgentDefinition, string>({
      query: (id) => `/studio/agents/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Agent', id }],
    }),
    createAgent: builder.mutation<AgentDefinition, CreateAgentRequest>({
      query: (body) => ({ url: '/studio/agents', method: 'POST', body }),
      invalidatesTags: ['Agent'],
    }),
    updateAgent: builder.mutation<AgentDefinition, { id: string; body: UpdateAgentRequest }>({
      query: ({ id, body }) => ({ url: `/studio/agents/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Agent', id }, 'Agent'],
    }),
    publishAgent: builder.mutation<AgentDefinition, string>({
      query: (id) => ({ url: `/studio/agents/${id}/publish`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Agent', id }, 'Agent', 'Version'],
    }),
    rollbackAgent: builder.mutation<AgentDefinition, string>({
      query: (id) => ({ url: `/studio/agents/${id}/rollback`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Agent', id }, 'Agent', 'Version'],
    }),
    getAgentVersions: builder.query<AgentVersion[], string>({
      query: (id) => `/studio/agents/${id}/versions`,
      providesTags: ['Version'],
    }),
    listScripts: builder.query<AgentScript[], string>({
      query: (agentId) => `/studio/agents/${agentId}/scripts`,
      providesTags: ['Script'],
    }),
    createScript: builder.mutation<AgentScript, { agentId: string; body: CreateScriptRequest }>({
      query: ({ agentId, body }) => ({
        url: `/studio/agents/${agentId}/scripts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Script'],
    }),
    updateScript: builder.mutation<AgentScript, { id: string; body: UpdateScriptRequest }>({
      query: ({ id, body }) => ({ url: `/studio/scripts/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Script'],
    }),
    testScript: builder.mutation<{ success: boolean; output: string }, string>({
      query: (id) => ({ url: `/studio/scripts/${id}/test`, method: 'POST' }),
    }),
    activateScript: builder.mutation<AgentScript, string>({
      query: (id) => ({ url: `/studio/scripts/${id}/activate`, method: 'POST' }),
      invalidatesTags: ['Script'],
    }),
    deactivateScript: builder.mutation<AgentScript, string>({
      query: (id) => ({ url: `/studio/scripts/${id}/deactivate`, method: 'POST' }),
      invalidatesTags: ['Script'],
    }),
    testAgent: builder.mutation<AgentTestResult, AgentTestRequest>({
      query: ({ agentId, ...body }) => ({
        url: `/studio/agents/${agentId}/test`,
        method: 'POST',
        body,
      }),
    }),
    listAvailableSkills: builder.query<
      { skills: { name: string; slug: string; description: string }[] },
      undefined
    >({
      query: () => '/studio/skills',
      providesTags: ['Skill'],
    }),
  }),
});

export const {
  useListAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  usePublishAgentMutation,
  useRollbackAgentMutation,
  useGetAgentVersionsQuery,
  useListScriptsQuery,
  useCreateScriptMutation,
  useUpdateScriptMutation,
  useTestScriptMutation,
  useActivateScriptMutation,
  useDeactivateScriptMutation,
  useTestAgentMutation,
  useListAvailableSkillsQuery,
} = studioApi;
